import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import VideoPlayer from './VideoPlayer';

// Component for displaying and controlling video playback during recording review
const RecorderVideoPlayer = ({
    status,
    isSelectedVideoAvailable,
    libraryVideoId,
    videoSource,
    selectedThumbnail,
    updateStatus = () => { },
}) => {
    const videoPlayerRef = useRef(null);
    const [video, setVideo] = useState(null);
    const [startOffset, setStartOffset] = useState(0);
    const [options, setOptions] = useState({});
    const thumbnail = selectedThumbnail ?? video?.image;

    // Hook to fetch video data when libraryVideoId changes
    useEffect(() => {
        const fetchVideoData = async () => {
            try {
                const response = await fetch(`/api/library/${libraryVideoId}`);
                const data = await response.json();
                if (!data && videoPlayerRef.current) {
                    videoPlayerRef.current.load();
                }
                if (data.success) {
                    setVideo(data.data.video);
                }
            } catch (error) {
                console.error('Error fetching video data:', error);
            }
        };
        fetchVideoData();
    }, [libraryVideoId]);

    // Hook to update video player options when selectedThumbnail changes
    useEffect(() => {
        setOptions({
            thumbnail,
        });
    }, [selectedThumbnail]);

    // Hook to set startOffset and options when video changes
    useEffect(() => {
        if (video) {
            setStartOffset(video.video_untrimmed ? 0 : video.start_trim);
            setOptions({
                thumbnail,
                captions: video.caption_url,
                autoplayCaptions: video.autoplay_captions,
            });
        }
    }, [video]);

    // Function to handle video player errors
    const handleError = (err) => {
        let error = '';
        if (err) {
            error = err;
        } else if (videoPlayerRef.current) {
            error = videoPlayerRef.current.getError();
        }
        if (error.code === 3 || error.code === 4) {
            // unsupported video format error
        } else if (window.Bugsnag) {
            Bugsnag.notify(new Error('Video JS Error ' + error.message));
        }
        updateStatus('reviewFailed');
    };

    // Function to handle video player ready event
    const handleReady = () => {
        if (video && video.auto_start) {
            if (videoPlayerRef.current) {
                videoPlayerRef.current.play();
            }
        }
    };

    // Function to handle video player first frame event
    const handleFirstFrame = () => {
        if (startOffset > 0) {
            if (videoPlayerRef.current) {
                videoPlayerRef.current.seek(startOffset);
            }
        }
    };

    // Function to handle video player play event
    const handlePlay = () => {
        updateStatus('reviewing');
    };

    // Function to handle video player time update event
    const handleTime = (time) => {
        if (time.position < startOffset) {
            if (videoPlayerRef.current) {
                videoPlayerRef.current.seek(startOffset + 0.01);
            }
        }

        if (video && video.end_trim && !video.video_untrimmed) {
            if (time.position >= video.end_trim - 0.01 && !video.final_video && time.position < time.duration) {
                if (videoPlayerRef.current) {
                    videoPlayerRef.current.resetVideo();
                }
                updateStatus('beginReview');
            }
        }
    };

    // Function to handle video player completion event
    const handleComplete = () => {
        // Call resetVideo on the VideoPlayer component using the ref
        if (videoPlayerRef.current) {
            videoPlayerRef.current.resetVideo();
        }
        updateStatus('beginReview');
    };

    return (
        <>
            {/* Render the video player if video is available and status is 'reviewing' or 'beginReview' */}
            {
                isSelectedVideoAvailable && ['reviewing', 'beginReview'].includes(status) && (
                    <>
                        {
                            // Render video review container
                            <div className="video-review-container">
                                {
                                    // Render stop preview button if status is 'reviewing'
                                    status === 'reviewing' && <button className="fa-times-corner absolute btn--ghost bg-blue-48" type="button" onClick={handleComplete} aria-label="Stop Preview">
                                        <i className="fa fa-times font-white"></i>
                                    </button>
                                }

                                {
                                    // Render captions info if status is 'beginReview' and video has captions
                                    (status === 'beginReview' && video && (video.caption_processing || video.caption_url)) && (
                                        <div className="captions-info">

                                            {
                                                video.caption_processing && <div className="captions-pending"><i className="fa fa-commenting" aria-hidden="true"></i> Captioning In Progress</div>
                                            }

                                            {
                                                !video.caption_processing && video.caption_url && <div className="captions-complete">
                                                    <span className="fa-stack fa-stack-1x" >
                                                        <i className="fas fa-square fa-stack-1x" alt="Add Closed Captions"></i>
                                                        <i className="fas fa-closed-captioning font-size-64px fa-stack-1x" alt="Add Closed Captions"></i>
                                                    </span>
                                                </div>
                                            }
                                        </div>
                                    )
                                }
                                {/* Render VideoPlayer component */}
                                <div id="recorder-review-wrapper" video-transform="true" video="video"></div>
                                <VideoPlayer ref={videoPlayerRef} vidId="recorder-review" videoUrl={videoSource} additionalOptions={options} onError={handleError} onReady={handleReady} onFirstFrame={handleFirstFrame} onPlay={handlePlay} onTime={handleTime} onComplete={handleComplete} />
                            </div>
                        }
                    </>
                )
            }
        </>
    );
};

RecorderVideoPlayer.propTypes = {
    status: PropTypes.string,
    libraryVideoId: PropTypes.number,
    isSelectedVideoAvailable: PropTypes.bool,
    videoSource: PropTypes.string,
    animatedThumbPath: PropTypes.string,
    selectedThumbnail: PropTypes.string,
    updateStatus: PropTypes.func,
};

export default RecorderVideoPlayer;
